import { useOutsideClick, HStack } from '@chakra-ui/react';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import 'react-dates/initialize';
import { FocusedInputShape } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';

import DateArrowIcon from './date-arrow-icon/date-arrow-icon';
import { DateInput } from './date-input/date-input';
import {
    DateMoment,
    DatepickerProps
} from './datepicker.types';
import { t } from "@lingui/macro";
import { useAppSelector } from '../../store/store';
import { parseDate } from './date-input/date-picker-utils';
import { DATE_FORMAT_DD_MM_YYYY } from '../../utils/consts';

type FocusedInput = FocusedInputShape | null;

export const Datepicker = ({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    academicYearStartDate,
    academicYearEndDate,
    academicEndDateForCurrentSelection,
    setAcademicEndDateForCurrentSelection
}: DatepickerProps) => {
    const localStartDate = startDate && moment(startDate).local();
    const localEndDate = endDate && moment(endDate).local();
    const localAcademicYearStartDate = startDate && moment(academicYearStartDate).local();
    const currentMoment = moment().local();
    const stackRef = useRef<HTMLDivElement | null>(null);
    const [focusedInput, setFocusedInput] = useState<FocusedInput>(null);
    const {
        timeFilter
    } = useAppSelector(
        rootState => rootState.filtersContainerSlice
    );
    function getCurrentDateTime(date: DateMoment) {
        return moment(date).local().set({
            hour: currentMoment.hour(),
            minute: currentMoment.minute(),
            second: currentMoment.second()
        });
    }

    function getEndDateForSelectedDate(selectedDate: DateMoment): string {
            const selected = selectedDate?.clone().startOf('day');

            if (!selected) return moment().format(DATE_FORMAT_DD_MM_YYYY);

            const match = timeFilter.data.find((d: { startDate?: string; endDate?: string }) => {
                if (!d.startDate || !d.endDate) return false;
                const start = parseDate(d.startDate);
                const end = parseDate(d.endDate);
                return selected.isSameOrAfter(start, 'day') && selected.isSameOrBefore(end, 'day')
            });

            return match?.endDate;
        };

    function onStartDateChangeHandler(date: DateMoment) {

        const isToday = currentMoment.isSame(date, 'day');

        if (isToday) {
            setStartDate(getCurrentDateTime(date));
        } else {
            setStartDate(moment(date).local().startOf('day'));
        }

        const updatedDate = parseDate(getEndDateForSelectedDate(date));
        setAcademicEndDateForCurrentSelection(moment(updatedDate));
        
        if (updatedDate?.local().isAfter(moment())) {
            setEndDate(moment());
        } else {
            setEndDate(updatedDate);
        }

        setFocusedInput(null);
    }

    function onEndDateChangeHandler(date: DateMoment) {
        const updatedDate = moment(date).local().endOf('day');

        setEndDate(updatedDate);
        setFocusedInput(null);
    }

    useOutsideClick({
        ref: stackRef,
        handler: () => setFocusedInput(null)
    });

    return (
        <HStack ref={stackRef} spacing={'5'} justifyContent={'center'}>
            <DateInput
                isHighlighted={focusedInput !== 'endDate' && !localStartDate}
                labelText={t`timePeriod.start.date`}
                singleDatePickerProps={{
                    date: localStartDate,
                    focused: focusedInput === 'startDate',
                    onDateChange: (date) => onStartDateChangeHandler(date),
                    isOutsideRange: (day) => day?.local().isBefore(academicYearStartDate, "day") || day?.local().isAfter(moment()) || day?.local().isBefore(localAcademicYearStartDate, 'day'),
                    isDayHighlighted: (day: moment.MomentInput) => moment(day).isSameOrAfter(localStartDate) && moment(day).isSameOrBefore(localEndDate),
                    initialVisibleMonth: () => moment(startDate).local()
                }}
                onClick={() => setFocusedInput('startDate')}
            />
            <DateArrowIcon />
            <DateInput
                isHighlighted={!localEndDate}
                labelText={t`timePeriod.end.date`}
                singleDatePickerProps={{
                    date: localEndDate,
                    focused: focusedInput === 'endDate',
                    onDateChange: (date) => onEndDateChangeHandler(date),
                    isOutsideRange: (day) => day?.local().isAfter(academicEndDateForCurrentSelection, "day") || day?.local().isAfter(moment()) || day?.local().isBefore(localStartDate, 'day'),
                    isDayHighlighted: (day: moment.MomentInput) => moment(day).isSameOrAfter(localStartDate) && moment(day).isSameOrBefore(localEndDate),
                    initialVisibleMonth: () => moment(endDate)
                }}
                onClick={() => setFocusedInput('endDate')}
            />
        </HStack>
    );
};
